import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";

export const DISCLOSURE = "Temple of the Human Heart is a for-profit organization. Gifts made through this page are not represented as tax-deductible charitable contributions.";
export const money = (cents, cur = "usd") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur.toUpperCase() }).format((cents || 0) / 100);

export default function Give() {
  const { user } = useApp();
  const [sp] = useSearchParams();
  const [cfg, setCfg] = useState({ suggested_amounts: [10, 25, 50, 100], min_amount: 1 });
  const [type, setType] = useState("one_time");
  const [amount, setAmount] = useState(25);
  const [other, setOther] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", marketing_consent: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/giving/config").then((r) => { setCfg(r.data); if (r.data.suggested_amounts?.[1]) setAmount(r.data.suggested_amounts[1]); }).catch(() => {}); }, []);
  useEffect(() => {
    if (user) { const parts = (user.name || "").trim().split(/\s+/); setForm((s) => ({ ...s, first_name: parts[0] || "", last_name: parts.slice(1).join(" "), email: user.email || "" })); }
  }, [user]);
  useEffect(() => { if (sp.get("cancelled")) toast("No gift was made. You are welcome anytime."); }, [sp]);

  const chosen = other !== "" ? Number(other) : amount;
  const valid = chosen >= cfg.min_amount && form.first_name && form.last_name && /.+@.+\..+/.test(form.email);

  const give = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const { data } = await api.post("/giving/checkout", { giving_type: type, amount: chosen, ...form, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "We couldn't start checkout. Please try again."); setBusy(false); }
  };

  const Seg = ({ v, label, sub }) => (
    <button type="button" data-testid={`give-type-${v}-btn`} onClick={() => setType(v)} className="flex-1 rounded-2xl border px-4 py-4 text-left transition-colors"
      style={{ background: type === v ? "var(--burgundy)" : "transparent", color: type === v ? "var(--bg-cream)" : "var(--text-obsidian)", borderColor: type === v ? "var(--burgundy)" : "var(--border-cream)" }}>
      <div className="font-serif text-lg">{label}</div>
      <div className="text-xs mt-0.5 opacity-80">{sub}</div>
    </button>
  );

  return (
    <div data-testid="give-page">
      <section className="relative overflow-hidden" style={{ background: "var(--surface-deep)" }}>
        <div className="grain absolute inset-0" /><div className="bloom absolute inset-0 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 relative" style={{ color: "var(--bg-cream)" }}>
          <div className="eyebrow mb-3" style={{ color: "var(--gold-soft)" }}>Support the Temple</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">If this work has supported your journey, you are welcome to give.</h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl opacity-90">
            Temple of the Human Heart offers teachings, gatherings, Temple Radio, podcast conversations, writings, and spaces for awakening and exploration. Giving helps sustain this work. Giving is optional.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 rounded-3xl border p-6 sm:p-8" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="give-form">
          <div className="eyebrow mb-3">How would you like to give?</div>
          <div className="flex gap-3">
            <Seg v="one_time" label="One-time gift" sub="A single gift, today." />
            <Seg v="monthly" label="Monthly support" sub="Renews each month. Cancel anytime." />
          </div>

          <div className="eyebrow mt-8 mb-3">Amount</div>
          <div className="grid grid-cols-4 gap-2">
            {cfg.suggested_amounts.map((a) => (
              <button key={a} type="button" data-testid={`give-amount-${a}-btn`} onClick={() => { setAmount(a); setOther(""); }} className="rounded-xl border py-3 font-serif text-lg"
                style={{ background: other === "" && amount === a ? "var(--gold)" : "transparent", color: "var(--burgundy)", borderColor: other === "" && amount === a ? "var(--gold)" : "var(--border-cream)" }}>${a}</button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-sm" style={{ color: "var(--text-muted)" }}>Other amount</span>
            <div className="relative flex-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "var(--text-muted)" }}>$</span>
              <input data-testid="give-other-amount-input" type="number" min={cfg.min_amount} step="1" value={other} onChange={(e) => setOther(e.target.value)} placeholder="" className="w-full rounded-lg border pl-7 pr-3 py-2.5 text-sm bg-transparent" style={{ borderColor: "var(--border-cream)" }} /></div>
          </div>
          {other !== "" && chosen < cfg.min_amount && <p className="text-xs mt-2" style={{ color: "var(--rose-dark)" }}>The minimum gift is ${cfg.min_amount.toFixed(2)}.</p>}

          <div className="eyebrow mt-8 mb-3">About you</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="give-first-name-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" className="rounded-lg border px-4 py-3 text-sm bg-transparent" style={{ borderColor: "var(--border-cream)" }} />
            <input data-testid="give-last-name-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" className="rounded-lg border px-4 py-3 text-sm bg-transparent" style={{ borderColor: "var(--border-cream)" }} />
            <input data-testid="give-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="sm:col-span-2 rounded-lg border px-4 py-3 text-sm bg-transparent" style={{ borderColor: "var(--border-cream)" }} />
          </div>
          <label className="mt-4 flex items-start gap-3 text-sm cursor-pointer" style={{ color: "var(--text-obsidian)" }}>
            <input data-testid="give-marketing-consent-checkbox" type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })} className="mt-1" />
            <span>Keep me connected with Temple of the Human Heart by email. <span style={{ color: "var(--text-muted)" }}>(optional)</span></span>
          </label>

          <div className="mt-8 rounded-2xl border p-4 flex items-center justify-between gap-4" style={{ borderColor: "var(--gold)", background: "var(--bg-cream)" }} data-testid="give-summary">
            <div>
              <div className="eyebrow">You selected</div>
              <div className="font-serif text-xl" style={{ color: "var(--burgundy)" }} data-testid="give-summary-text">
                {type === "monthly" ? "Monthly support" : "One-time gift"} · {chosen > 0 ? money(Math.round(chosen * 100)) : "—"}{type === "monthly" ? " / month" : ""}
              </div>
            </div>
            <Heart className="w-6 h-6" style={{ color: "var(--gold-dark)" }} />
          </div>
          <button data-testid="give-submit-btn" disabled={!valid || busy} onClick={give} className="mt-4 w-full rounded-full py-3.5 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{type === "monthly" ? "Continue to monthly support" : "Continue to give"}
          </button>
          <p className="mt-3 text-xs text-center" style={{ color: "var(--text-muted)" }}>Payment is handled securely by Stripe. The Temple never stores your card details.</p>
          <p className="mt-4 text-xs leading-relaxed" style={{ color: "var(--text-muted)" }} data-testid="give-disclosure">{DISCLOSURE}</p>
        </div>

        <aside className="lg:col-span-2 space-y-6">
          <div className="rounded-3xl border p-6" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
            <div className="eyebrow mb-2">What giving sustains</div>
            <ul className="space-y-2 text-sm leading-relaxed" style={{ color: "var(--text-obsidian)" }}>
              <li>Temple Radio and the podcast conversations</li><li>Public teachings, writings, and the Library</li><li>Gatherings, online and in person</li><li>The Learning Circle and its Companion</li>
            </ul>
          </div>
          <div className="rounded-3xl p-6" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
            <div className="eyebrow mb-2" style={{ color: "var(--gold-soft)" }}>Monthly support</div>
            <p className="text-sm leading-relaxed opacity-90">Monthly support renews automatically. You may cancel at any time; cancellation takes effect at the end of the current billing period, and no further charges are made.</p>
          </div>
          <p className="text-xs italic px-2" style={{ color: "var(--text-muted)" }}>Giving is not a condition of belonging. Everything public here remains freely offered.</p>
        </aside>
      </section>
    </div>
  );
}
